import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled from 'styled-components'

import { Array, AsyncStateN } from '@blade/std-lib'
import {
  openPremiumCurrencyDetailsEvent,
  openBuyPremiumFlowEvent,
  openExtendPremiumFlowEvent,
} from '@root/entities/premium'
import {
  ActivePremiumCard,
  ActivePremiumCardSkeleton,
  ExpiredPremiumCard,
  ExpiredPremiumCardSkeleton,
} from '@root/entities/premium/ui'
import {
  userPremiumsModelAtom,
  userPremiumTabAtom,
} from '@root/features/premium'
import { premiumCurrenciesStateAtom } from '@root/features/premium/user-premium-list'
import { FadeOverflow, InfoMessage } from '@root/shared/ui-kit'

import { PremiumScreenActiveDrawer } from '../../premium-screen/ui/premium-screen/premium-screen-active-drawer'

import type { CurrencyWithPremium } from '@root/entities/currency'

const placeholders = Array.range(1, 6)

export const UserPremiumList: React.FC = () => {
  const [model] = useAtom(userPremiumsModelAtom)
  const [currenciesState] = useAtom(premiumCurrenciesStateAtom)
  const loadUserPremiums = useAction(model.run)
  const openPremiumCurrencyDetails = useAction(openPremiumCurrencyDetailsEvent)
  const openBuyPremiumDrawer = useAction(openBuyPremiumFlowEvent)
  const openExtendPremiumDrawer = useAction(openExtendPremiumFlowEvent)
  const handleCurrency = (
    currency: CurrencyWithPremium.CurrencyWithPremium,
  ) => {
    openPremiumCurrencyDetails({ currencyAddress: currency.currency.address })
  }
  const [openActiveDrawer, setOpenActiveDrawer] = useState(false)

  const [tab] = useAtom(userPremiumTabAtom)

  return AsyncStateN.matchWaiting(currenciesState, {
    Waiting: () => (
      <Root>
        <Overflow variant="vertical">
          {placeholders.map((it) =>
            tab === 'active' ? (
              <ActivePremiumCardSkeleton key={it} />
            ) : (
              <ExpiredPremiumCardSkeleton key={it} />
            ),
          )}
        </Overflow>
      </Root>
    ),
    Error: () => (
      <InfoContainer>
        <InfoMessage intent="error" onRetry={loadUserPremiums} />
      </InfoContainer>
    ),
    Success: (currencies) =>
      currencies.length === 0 ? (
        <InfoContainer>
          <InfoMessage intent="empty" />
        </InfoContainer>
      ) : (
        <Root>
          <Overflow variant="vertical">
            {currencies.map((meta) => {
              if (tab === 'active') {
                return (
                  <ActivePremiumCard
                    key={meta.currencyAddress}
                    currency={meta.currency}
                    onEdit={handleCurrency}
                    onExtend={openExtendPremiumDrawer}
                    premium={meta}
                    onActiveClick={() => setOpenActiveDrawer(true)}
                  />
                )
              }
              return (
                <ExpiredPremiumCard
                  key={meta.currencyAddress}
                  currency={meta.currency}
                  premium={meta}
                  onBuyAgain={(currency) => {
                    openBuyPremiumDrawer({
                      ...currency,
                      shouldCheckSafety: false,
                    })
                  }}
                />
              )
            })}
          </Overflow>
          <PremiumScreenActiveDrawer
            open={openActiveDrawer}
            onOpenChange={setOpenActiveDrawer}
          />
        </Root>
      ),
  })
}

const Root = styled.div`
  display: grid;
  height: 100%;
  min-height: 0;
`

const Overflow = styled(FadeOverflow)`
  display: grid;
  align-content: start;
  gap: 9px;

  height: 100%;
  min-height: 0;
  padding: 12px;
  overflow-y: auto;
`

const InfoContainer = styled.div`
  display: grid;
  height: 100%;
  min-height: 0;
`
